import { useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { OWASP_2025_CATEGORIES, normalizeOwaspStatus } from '@/lib/owasp';
import { NAV_LABELS, STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';
import { isKnownControlKey, resolveControlKey } from '@/lib/controlRegistry';

const statusMeta = (status: string) => {
  switch (status) {
    case 'action':
      return { label: STATUS_LABELS.actionRequired, tone: 'danger' as const, icon: ShieldAlert };
    case 'partial':
      return { label: STATUS_LABELS.partial, tone: 'warning' as const, icon: ShieldAlert };
    case 'covered':
      return { label: STATUS_LABELS.covered, tone: 'success' as const, icon: ShieldCheck };
    default:
      return { label: STATUS_LABELS.monitoringPlanned, tone: 'neutral' as const, icon: Shield };
  }
};

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

export default function DomainOverview() {
  const navigate = useNavigate();
  const { domain } = useOutletContext<{ domain: { id: string; domain_name: string } }>();

  const logsQuery = useQuery({
    queryKey: ['domain-owasp-logs', domain.id],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('owasp_id, control_key, status, recommended_action, last_checked_at, created_at')
        .eq('domain_id', domain.id)
        .not('owasp_id', 'is', null)
        .gte('created_at', cutoff)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const securityControlsQuery = useQuery({
    queryKey: ['domain-security-controls', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_logs')
        .select('control_key, status, enforced, evidence, last_checked_at, created_at')
        .eq('domain_id', domain.id)
        .eq('category', 'security_control')
        .in('control_key', [
          'autoupdate_plugins_detect',
          'password_policy_enforced',
          'admin_geo_guard',
          'mfa_coverage_snapshot',
          'geo_policy_detect',
          'mfa_coverage',
        ])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const postureQuery = useQuery({
    queryKey: ['domain-security-posture', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_security_posture')
        .select('coverage_score, posture_score, coverage_breakdown, posture_breakdown, computed_at')
        .eq('domain_id', domain.id)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      if (data) return data;
      const { data: computed, error: computeError } = await supabase.functions.invoke('security-posture', {
        body: { domain_id: domain.id },
      });
      if (computeError) {
        if (computeError.status === 401) {
          navigate('/portal/login', { replace: true });
          throw new Error('Session expired');
        }
        throw computeError;
      }
      return computed;
    },
  });

  const mfaSnapshotQuery = useQuery({
    queryKey: ['domain-mfa-coverage', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_logs')
        .select('metadata, created_at')
        .eq('domain_id', domain.id)
        .eq('event_type', 'mfa_coverage_snapshot')
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
  });

  const summary = useMemo(() => {
    const logs = logsQuery.data || [];
    const latestByControl = new Map<string, { owasp_id: string; status: string; created_at: string; last_checked_at?: string | null; }>();

    logs.forEach((log) => {
      if (!log.control_key) return;
      const current = latestByControl.get(log.control_key);
      if (!current || log.created_at > current.created_at) {
        latestByControl.set(log.control_key, {
          owasp_id: log.owasp_id,
          status: log.status || 'ok',
          created_at: log.created_at,
          last_checked_at: log.last_checked_at,
        });
      }
    });

    const categoryStatus = new Map<string, string>();
    let lastChecked = '';

    latestByControl.forEach((control) => {
      const normalized = normalizeOwaspStatus(control.status);
      const existing = categoryStatus.get(control.owasp_id);
      if (!existing || (existing !== 'critical' && normalized === 'critical') || (existing === 'ok' && normalized === 'warn')) {
        categoryStatus.set(control.owasp_id, normalized === 'resolved' ? 'ok' : normalized);
      }
      const candidate = control.last_checked_at || control.created_at;
      if (candidate && candidate > lastChecked) lastChecked = candidate;
    });

    let overall = 'planned';
    const statuses = Array.from(categoryStatus.values());
    if (statuses.includes('critical')) overall = 'action';
    else if (statuses.includes('warn')) overall = 'partial';
    else if (statuses.length > 0) overall = 'covered';

    const labelMap: Record<string, string> = {
      A01: 'Account & access protection',
      A02: 'Transport & crypto posture',
      A03: 'Input safety signals',
      A04: 'Security guardrails',
      A05: 'Configuration hygiene',
      A06: 'Updates & integrity',
      A07: 'Authentication hardening',
      A08: 'Integrity checks',
      A09: 'Monitoring readiness',
      A10: 'Request controls',
    };

    const topItems = OWASP_2025_CATEGORIES
      .filter((category) => {
        const status = categoryStatus.get(category.id);
        return status === 'critical' || status === 'warn';
      })
      .slice(0, 3)
      .map((category) => labelMap[category.id] || category.name);

    return {
      overall,
      lastChecked,
      topItems,
      categoryCount: categoryStatus.size,
    };
  }, [logsQuery.data]);

  const meta = statusMeta(summary.overall);
  const MetaIcon = meta.icon;
  const mfaDetails = (mfaSnapshotQuery.data?.metadata as any)?.details ?? {};
  const mfaTotal = Number(mfaDetails.admin_total ?? 0);
  const mfaEnrolled = Number(mfaDetails.admin_enrolled ?? 0);
  const mfaPercent = Number(mfaDetails.coverage_percent ?? 0);
  const mfaProvider = String(mfaDetails.provider ?? '');
  const mfaLastChecked = mfaDetails.last_checked_at ?? mfaSnapshotQuery.data?.created_at ?? null;

  let mfaStatus: { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' } = {
    label: STATUS_LABELS.monitoringPlanned,
    variant: 'neutral',
  };
  if (mfaProvider && mfaTotal > 0) {
    if (mfaPercent >= 90) {
      mfaStatus = { label: STATUS_LABELS.covered, variant: 'success' };
    } else if (mfaPercent >= 50) {
      mfaStatus = { label: STATUS_LABELS.partial, variant: 'warning' };
    } else {
      mfaStatus = { label: STATUS_LABELS.actionRequired, variant: 'danger' };
    }
  } else if (mfaSnapshotQuery.data) {
    mfaStatus = { label: STATUS_LABELS.partial, variant: 'warning' };
  }

  const latestByControl = useMemo(() => {
    const map = new Map<string, any>();
    const unknown = new Set<string>();
    (securityControlsQuery.data || []).forEach((entry: any) => {
      if (!entry.control_key) return;
      const normalized = resolveControlKey(entry.control_key);
      if (!isKnownControlKey(normalized)) {
        unknown.add(entry.control_key);
        return;
      }
      if (!map.has(normalized)) {
        map.set(normalized, { ...entry, control_key: normalized });
      }
    });
    if (import.meta.env.DEV && unknown.size > 0) {
      console.warn('Unknown security control keys detected', Array.from(unknown));
    }
    return map;
  }, [securityControlsQuery.data]);

  const autoUpdate = latestByControl.get('autoupdate_plugins_detect');
  const autoUpdateEvidence = autoUpdate?.evidence ?? {};
  const autoUpdateLabel = autoUpdateEvidence.enabled ? 'On' : 'Off';
  const autoUpdateMode = autoUpdateEvidence.mode ? String(autoUpdateEvidence.mode) : 'allowlist';

  const passwordPolicy = latestByControl.get('password_policy_enforced');
  const passwordEvidence = passwordPolicy?.evidence ?? {};
  const passwordLabel = passwordEvidence.enforce_min_length || passwordEvidence.enforce_breached
    ? 'Enforced on change/reset'
    : 'Not enforced';
  const passwordMin = typeof passwordEvidence.min_length === 'number' ? passwordEvidence.min_length : 12;
  const breachBlocking = passwordEvidence.enforce_breached ? 'Enabled' : 'Disabled';

  const adminGeo = latestByControl.get('admin_geo_guard');
  const adminGeoEvidence = adminGeo?.evidence ?? {};
  const adminGeoPolicy = adminGeoEvidence.policy ?? {};
  const adminGeoMode = adminGeoPolicy.mode ? String(adminGeoPolicy.mode) : 'observe';
  const adminGeoAllowCount = Array.isArray(adminGeoPolicy.allow_countries) ? adminGeoPolicy.allow_countries.length : 0;
  const adminGeoDenyCount = Array.isArray(adminGeoPolicy.deny_countries) ? adminGeoPolicy.deny_countries.length : 0;

  return (
    <div className="grid gap-6">
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Coverage & posture</CardTitle>
          <CardDescription>Two explainable scores based on live signals.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div>
              <div className="text-foreground font-semibold">Coverage</div>
              <div>{postureQuery.data?.coverage_score ?? 0}/100</div>
            </div>
            <div>
              <div className="text-foreground font-semibold">Posture</div>
              <div>{postureQuery.data?.posture_score ?? 0}/100</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Computed {postureQuery.data?.computed_at ? formatTimestamp(postureQuery.data?.computed_at) : 'Unknown'}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MetaIcon className="h-5 w-5" />
            Security Health
          </CardTitle>
          <CardDescription>Based on protection coverage signals and the last 30 days of activity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant={meta.tone}>{meta.label}</Badge>
            <span className="text-sm text-muted-foreground">Coverage checks reported: {summary.categoryCount}/10</span>
          </div>
          <div className="text-sm text-muted-foreground">Last coverage check: {formatTimestamp(summary.lastChecked)}</div>
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
            <div className="font-semibold">Top items</div>
            {summary.topItems.length === 0 ? (
              <div className="text-muted-foreground">No active coverage gaps detected.</div>
            ) : (
              <ul className="list-disc list-inside text-muted-foreground">
                {summary.topItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          <Button variant="outline" onClick={() => navigate(`/portal/domains/${domain.id}/coverage`)}>
            View {NAV_LABELS.protectionCoverage}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>MFA coverage</CardTitle>
          <CardDescription>Admin MFA enrollment status reported by the connector.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge variant={mfaStatus.variant}>{mfaStatus.label}</Badge>
            <span className="text-sm text-muted-foreground">
              Admins with MFA: {mfaTotal > 0 ? `${mfaEnrolled} / ${mfaTotal}` : 'Unknown'}
              {mfaTotal > 0 ? ` (${mfaPercent.toFixed(1)}%)` : ''}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">Provider: {mfaProvider || 'Unknown'}</div>
          <div className="text-sm text-muted-foreground">Last checked: {formatTimestamp(mfaLastChecked)}</div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Additional security controls</CardTitle>
          <CardDescription>Signals based on plugin configuration and policy.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-center justify-between gap-3">
            <span>Plugin auto-updates</span>
            <span className="text-foreground">
              {autoUpdateLabel} · Mode: {autoUpdateMode === 'all' ? 'All' : 'Allowlist'} · Themes excluded
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Password policy (change/reset)</span>
            <span className="text-foreground">
              {passwordLabel} · Min {passwordMin} (admins) · Breached blocking: {breachBlocking}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Admin access location rules</span>
            <span className="text-foreground">
              {adminGeoMode === 'enforce' ? 'Enforce' : 'Observe'} · Allowlist: {adminGeoAllowCount} · Denylist: {adminGeoDenyCount}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
